import { assertRequiredColumns } from '../assertRequiredColumns';
import {
  asChartData,
  axisNumberFormat,
  chartPresentationProps,
} from '../presentation';
import { RenderAdapterError } from '../types';
import type {
  RenderAdapterResult,
  ScatterAdapterConfig,
  ScatterAdapterProps,
} from '../types';
import { validateRowsInput } from '../validateRowsInput';

/**
 * The series key a scatter's `config` is looked up by when the renderer is not
 * splitting the points into groups. Matches the renderer's own default, so an
 * authored label reaches the legend without the host naming the key.
 */
const DEFAULT_SERIES_KEY = 'default';

function scatterProps(
  config: ScatterAdapterConfig | undefined,
  data: ScatterAdapterProps['data'],
): ScatterAdapterProps {
  const presentation = config?.presentation;
  const yAxisKey = config?.yAxisKey ?? '';

  return {
    ...presentation?.passthrough,
    data,
    // A scatter has one implicit series, so the label falls back through what
    // the author named it, then what the axis is called, then the column — the
    // legend should never render an empty entry.
    config: {
      [DEFAULT_SERIES_KEY]: {
        label:
          presentation?.seriesLabel ?? presentation?.yAxisLabel ?? yAxisKey,
      },
    },
    xAxisKey: config?.xAxisKey ?? '',
    yAxisKey,
    xAxisLabel: presentation?.xAxisLabel,
    yAxisLabel: presentation?.yAxisLabel,
    xAxisDomain: presentation?.xAxisDomain
      ? [...presentation.xAxisDomain]
      : undefined,
    yAxisDomain: presentation?.yAxisDomain
      ? [...presentation.yAxisDomain]
      : undefined,
    xAxisFormat: axisNumberFormat(presentation?.xAxisFormat),
    yAxisFormat: axisNumberFormat(presentation?.yAxisFormat),
    ...chartPresentationProps(presentation),
  };
}

/**
 * Scatter points, passed through flat.
 *
 * Unlike every other chart here there is no pivot and no series list: the
 * renderer groups the rows itself when a grouping column is authored, and it
 * maps raw group values to safe color tokens internally. Reshaping the rows
 * first would take that away from it.
 */
export function scatterAdapter(
  rows: unknown,
  config: ScatterAdapterConfig,
): RenderAdapterResult<ScatterAdapterProps> {
  const validated = validateRowsInput(rows, scatterProps(config, []));
  if ('props' in validated) {
    return validated;
  }

  // Both axes are required rather than inferred. A scatter with one axis bound
  // is not a chart with a missing detail — there is no plot to draw — and
  // inferring the other axis from the rows would draw a relationship the author
  // never described.
  const missingConfig = [
    ['xAxisKey', config.xAxisKey],
    ['yAxisKey', config.yAxisKey],
  ]
    .filter(([, value]) => typeof value !== 'string' || value.length === 0)
    .map(([name]) => name as string);

  if (missingConfig.length > 0) {
    return {
      props: scatterProps(config, []),
      error: new RenderAdapterError(
        'missing_columns',
        missingConfig,
        `Scatter needs both an X and a Y metric; missing: ${missingConfig.join(', ')}`,
      ),
    };
  }

  const columnError = assertRequiredColumns(validated.rows, [
    config.xAxisKey,
    config.yAxisKey,
  ]);
  if (columnError) {
    return { props: scatterProps(config, []), error: columnError };
  }

  return { props: scatterProps(config, asChartData(validated.rows)) };
}
